import { useEffect, useRef } from 'react';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import { Image as BaseImage } from '@tiptap/extension-image';
import { Wrench } from 'lucide-react';

type ResizeAnchor = 'br' | 'bl' | 'tr' | 'tl';

const ResizableImageView = ({ node, updateAttributes, selected, editor, getPos }: any) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const startRef = useRef<{ anchor: ResizeAnchor; startX: number; rect: DOMRect } | null>(null);

  const startResize = (anchor: ResizeAnchor, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!imgRef.current) return;

    const rect = imgRef.current.getBoundingClientRect();
    startRef.current = { anchor, startX: e.clientX, rect };

    const onMove = (ev: MouseEvent) => {
      if (!startRef.current || !imgRef.current) return;
      const { anchor: currentAnchor, rect: currentRect } = startRef.current;
      let newWidth = imgRef.current.width;

      if (currentAnchor === 'br' || currentAnchor === 'tr') {
        newWidth = Math.max(50, ev.clientX - currentRect.left);
      } else {
        newWidth = Math.max(50, currentRect.right - ev.clientX);
      }

      updateAttributes({ width: Math.round(newWidth) });
    };

    const onUp = () => {
      startRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  useEffect(() => {
    if (!imgRef.current) return;
    const base = 'max-width:100%;height:auto;display:block;';
    const width = node.attrs.width ? `width:${node.attrs.width}px;` : '';
    const custom = typeof node.attrs.style === 'string' ? node.attrs.style : '';
    imgRef.current.setAttribute('style', `${base}${width}${custom}`);
  }, [node.attrs.width, node.attrs.style]);

  return (
    <NodeViewWrapper className={`group relative inline-block ${selected ? 'ring-2 ring-blue-400' : ''}`}>
      <img
        ref={imgRef}
        src={node.attrs.src}
        alt={node.attrs.alt || ''}
        style={{ width: node.attrs.width ? `${node.attrs.width}px` : undefined, maxWidth: '100%', height: 'auto', display: 'block' }}
        draggable={false}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          try {
            if (editor && typeof getPos === 'function') {
              editor.chain().focus().setNodeSelection(getPos()).run();
            }
          } catch {
            // ignore
          }
        }}
      />
      <div className="absolute right-2 top-2 z-10 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100 pointer-events-auto">
        <button
          type="button"
          title="Image tools"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md border bg-background/80 text-foreground shadow-sm transition-colors hover:bg-background"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            try {
              if (editor && typeof getPos === 'function') {
                editor.chain().focus().setNodeSelection(getPos()).run();
              }
            } catch {
              // ignore
            }
            window.dispatchEvent(new CustomEvent('rte-open-image-tools'));
          }}
        >
          <Wrench className="h-4 w-4" />
        </button>
      </div>
      <span className="absolute -left-1.5 -top-1.5 h-3 w-3 cursor-nwse-resize rounded-full border bg-background" onMouseDown={(e) => startResize('tl', e)} />
      <span className="absolute -right-1.5 -top-1.5 h-3 w-3 cursor-nesw-resize rounded-full border bg-background" onMouseDown={(e) => startResize('tr', e)} />
      <span className="absolute -bottom-1.5 -left-1.5 h-3 w-3 cursor-nesw-resize rounded-full border bg-background" onMouseDown={(e) => startResize('bl', e)} />
      <span className="absolute -bottom-1.5 -right-1.5 h-3 w-3 cursor-nwse-resize rounded-full border bg-background" onMouseDown={(e) => startResize('br', e)} />
    </NodeViewWrapper>
  );
};

const ResizableImage = BaseImage.extend({
  name: 'resizableImage',
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        renderHTML: (attributes) => (attributes.width ? { width: attributes.width } : {}),
        parseHTML: (element) => {
          const width = element.getAttribute('width');
          if (width) return parseInt(width, 10);
          const style = element.getAttribute('style') || '';
          const match = style.match(/width\s*:\s*(\d+)\s*px/i);
          return match ? parseInt(match[1], 10) : null;
        },
      },
      alt: { default: null },
      src: { default: null },
      style: {
        default: '',
        renderHTML: (attributes) => (attributes.style ? { style: attributes.style } : {}),
        parseHTML: (element) => element.getAttribute('style') || '',
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageView);
  },
});

export default ResizableImage;